import { CheckmarkIcon, MinusIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import {
  Button,
  DISABLED_CLASSES,
  Paragraph,
  ParagraphWithTooltip,
  fontWeights,
  paragraphSizes,
} from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useCheckOverflow, useOutsideClick } from '@va/util/hooks';
import classNames from 'classnames';
import React, { FormEvent, PropsWithChildren, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

type InputWithSubsectionProps = {
  wrapperClasses?: string;
  onSubmit: (e: FormEvent) => void;
  onRemove: (value: string) => void;
  listItems: string[];
  itemsLabel: string;
  icon?: React.ReactNode;
  inputProps: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    name: string;
    label: string;
    id?: string;
    required?: boolean;
  };
  successSubmit: boolean;
  error?: string;
  isLoading?: boolean;
  placeholdersCount?: number;
  disabled?: boolean;
  infoTooltip?: string;
  listItemWrapperClass?: string;
};

export const InputWithSubsection = ({
  onSubmit,
  onRemove,
  icon,
  listItems,
  itemsLabel,
  wrapperClasses,
  successSubmit,
  error,
  isLoading,
  placeholdersCount = 4,
  disabled = false,
  infoTooltip,
  inputProps: { name, value, onChange, label, id, required, placeholder },
  listItemWrapperClass,
  children,
}: PropsWithChildren<InputWithSubsectionProps>) => {
  const [focused, setFocused] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useOutsideClick(formRef, () => setFocused(false));

  const translate = useTranslate();
  const inputLabelRef = useRef(null);
  const isOverflowing = useCheckOverflow(inputLabelRef);

  const isInputVisible = () => {
    if (!focused && !value) return false;
    if (!focused && value) return true;
    if (focused) return true;
    return false;
  };

  const isButtonDisabled = !value || successSubmit || isLoading || disabled;

  const isListEmpty = listItems.length === 0;

  const buttonContent = () => {
    if (successSubmit)
      return (
        <div className='flex items-center gap-1 justify-center'>
          <Paragraph weight={fontWeights.medium}>{translate('button.added')}</Paragraph>
          <CheckmarkIcon color='white' />
        </div>
      );

    return <Paragraph weight={fontWeights.medium}>{translate('button.addToList')}</Paragraph>;
  };

  const subsectionContent = useMemo(() => {
    if (children) return children;
    if (isLoading) {
      return (
        <div className={classNames('grid grid-cols-2 gap-1', listItemWrapperClass)}>
          {Array.from(Array(placeholdersCount).keys()).map((key) => (
            <Skeleton key={key} className='h-72 rounded-18' />
          ))}
        </div>
      );
    }
    return (
      <ul
        className={classNames(
          'p-2 grid grid-cols-2 gap-2 overflow-auto max-h-56.25 scrollbar scrollbar-thumb bg-white-snow',
          listItemWrapperClass,
          { hidden: isListEmpty },
        )}
      >
        {listItems.map((value, index) => {
          return (
            <ListItem
              infoTooltip={infoTooltip}
              disabled={disabled}
              key={index}
              value={value}
              label={itemsLabel}
              remove={onRemove}
            />
          );
        })}
      </ul>
    );
  }, [
    children,
    disabled,
    infoTooltip,
    isListEmpty,
    isLoading,
    itemsLabel,
    listItemWrapperClass,
    listItems,
    onRemove,
    placeholdersCount,
  ]);

  return (
    <div className={classNames(wrapperClasses)}>
      <div className='rounded-12 overflow-hidden bg-white-snow'>
        <div
          className={classNames('border  bg-white-snow transition-colors duration-500 py-2 px-3 rounded-t-12', {
            'border-primary bg-white': focused,
            'border-transparent': !focused,
            'rounded-b-12': listItems.length === 0,
          })}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (disabled) return;
              onSubmit(e);
            }}
            onClick={() => setFocused(true)}
            ref={formRef}
            className={classNames('flex items-center gap-2 justify-between overflow-hidden')}
          >
            {icon && icon}
            <div className='relative grow h-full'>
              <TooltipWrapper content={label} disabled={!isOverflowing}>
                <div
                  className={classNames(
                    'w-full absolute top-1/2 left-0 -translate-y-1/2 text-gray-charcoal font-medium hover:cursor-text transform transition-transform duration-500 overflow-hidden',
                    {
                      '-translate-y-5 text-xs text-primary': isInputVisible(),
                      'text-15': !isInputVisible(),
                    },
                  )}
                >
                  <label ref={inputLabelRef} htmlFor={id} className='block truncate'>
                    {label}
                  </label>
                </div>
              </TooltipWrapper>
              <input
                value={value}
                id={id}
                name={name}
                required={required}
                placeholder={placeholder}
                size={1}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                disabled={disabled}
                className={classNames(
                  'bg-transparent w-full translate translate-y-3 text-gray-charcoal font-medium truncate',
                  {
                    visible: isInputVisible(),
                    invisible: !isInputVisible(),
                  },
                )}
              />
            </div>
            <TooltipWrapper content={infoTooltip} disabled={!infoTooltip}>
              <button
                type='submit'
                className={classNames(
                  'py-12px px-18px rounded-15 text-gray-charcoal whitespace-nowrap font-medium hover:bg-gray-concrete transition-all duration-200 w-39 flex items-center justify-center',
                  {
                    'bg-white-snow': focused,
                    'bg-white': !focused,
                    'transform translate-x-52': isButtonDisabled && !successSubmit && focused,
                    'bg-green-pure! text-white!': successSubmit,
                  },
                  disabled && DISABLED_CLASSES,
                )}
              >
                {buttonContent()}
              </button>
            </TooltipWrapper>
          </form>
        </div>
        <div
          className={classNames('h-0.5', { hidden: isListEmpty })}
          style={{
            background: 'linear-gradient(90deg, #E1E1E1 0%, rgba(240, 240, 240, 0) 100%)',
          }}
        ></div>
        {subsectionContent}
      </div>
      {error && (
        <Paragraph className='text-red-pure ml-4 mt-1' size={paragraphSizes.tiny} weight={fontWeights.medium}>
          {error}
        </Paragraph>
      )}
    </div>
  );
};

type ListItemProps = {
  label: string;
  value: string;
  disabled?: boolean;
  infoTooltip?: string;
  remove: (value: string) => void;
};

export const ListItem = ({ label, value, remove, disabled = false, infoTooltip }: ListItemProps) => {
  return (
    <li className='flex items-center gap-2 justify-between p-2 bg-white rounded-15'>
      <div className='grow overflow-hidden'>
        <Paragraph
          className='truncate'
          size={paragraphSizes.tiny}
          weight={fontWeights.medium}
          colorClassName='text-gray-devil'
        >
          {label}
        </Paragraph>

        <ParagraphWithTooltip className='truncate' weight={fontWeights.medium}>
          {value}
        </ParagraphWithTooltip>
      </div>
      <Button
        color='tertiary'
        icon={() => <MinusIcon />}
        onClick={() => {
          remove(value);
        }}
        tooltip={infoTooltip}
        disabled={disabled}
        className='shrink-0 bg-white-snow rounded-12 w-12 h-12 flex items-center justify-center hover:bg-gray-concrete transition-all duration-200'
      />
    </li>
  );
};
